import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Image, Video, Infinity, Cpu, Check, ArrowUpRight } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import GradientHeroBg from './gradient/GradientHeroBg';

const ease = [0.22, 1, 0.36, 1];
const NOISE = "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E\")";

function GradientBg({ inverted = false, opacity = 1 }) {
  return (
    <div
      className="absolute inset-0 pointer-events-none overflow-hidden"
      style={{ opacity, transform: inverted ? 'rotate(180deg)' : 'none' }}
    >
      <div style={{ position: 'absolute', width: '900px', height: '600px', right: '-200px', top: '-50px', background: 'radial-gradient(ellipse, rgba(35,136,255,0.20) 0%, transparent 65%)', filter: 'blur(120px)', transform: 'rotate(-20deg)' }} />
      <div style={{ position: 'absolute', width: '800px', height: '580px', left: '-200px', top: '80px', background: 'radial-gradient(ellipse, rgba(74,58,255,0.16) 0%, transparent 65%)', filter: 'blur(120px)', transform: 'rotate(20deg)' }} />
      <div style={{ position: 'absolute', width: '1000px', height: '500px', left: '50%', bottom: '-80px', transform: 'translateX(-50%)', background: 'radial-gradient(ellipse, rgba(35,136,255,0.10) 0%, transparent 70%)', filter: 'blur(100px)' }} />
      <div style={{ position: 'absolute', inset: 0, backgroundImage: NOISE, backgroundSize: '200px 200px', opacity: 0.45 }} />
    </div>
  );
}

const navGlass = {
  backgroundColor: 'rgba(10,10,20,0.48)',
  backdropFilter: 'blur(20px) saturate(140%)',
  WebkitBackdropFilter: 'blur(20px) saturate(140%)',
  border: '1px solid rgba(255,255,255,0.12)',
  boxShadow: '0 16px 40px rgba(0,0,0,0.32), inset 0 1px 0 rgba(255,255,255,0.14)',
};

const glassCard = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.10)',
  backdropFilter: 'blur(16px) saturate(130%)',
  WebkitBackdropFilter: 'blur(16px) saturate(130%)',
};

const features = [
  {
    icon: Image,
    title: 'Generazione Immagini',
    description: 'Tutti i modelli fal.ai disponibili in un\'unica piattaforma. Da FLUX a Stable Diffusion, sempre aggiornati.',
  },
  {
    icon: Video,
    title: 'Generazione Video',
    description: 'Da testo a video cinematografico. Kling, Veo 3, Runway, Seedance — il meglio dell\'AI video.',
  },
  {
    icon: Infinity,
    title: 'Senza Censura',
    description: 'Libertà creativa totale. Nessun filtro arbitrario, nessuna restrizione su contenuti artistici.',
  },
  {
    icon: Cpu,
    title: 'Modelli Live',
    description: 'La lista dei modelli viene sincronizzata in tempo reale con fal.ai. Sempre aggiornata, sempre completa.',
  },
];

const pricingPlans = [
  {
    name: 'Starter',
    price: '0',
    period: 'per sempre',
    description: 'Per iniziare a esplorare',
    features: ['100 crediti di benvenuto', 'Tutti i modelli base', 'Galleria personale', 'Supporto community'],
    cta: 'Inizia Gratis',
    href: '/login',
    featured: false,
  },
  {
    name: 'Plus',
    price: '19',
    period: '/ mese',
    description: 'Per creativi seri',
    features: ['500 crediti / mese', 'Tutti i modelli premium', 'Risoluzione 4K', 'Batch processing', 'Supporto prioritario'],
    cta: 'Scegli Plus',
    href: '/login',
    featured: true,
  },
  {
    name: 'Pro',
    price: '49',
    period: '/ mese',
    description: 'Per professionisti',
    features: ['Crediti illimitati', 'Modelli esclusivi', 'API access', 'Commercial license', 'Supporto dedicato 24/7'],
    cta: 'Vai Pro',
    href: '/login',
    featured: false,
  },
];

function LandingNav({ isAuthenticated }) {
  return (
    <nav className="absolute top-0 inset-x-0 z-50 px-3 pt-3" style={{ fontFamily: 'var(--font-body)' }}>
      <div className="max-w-7xl mx-auto px-5 flex items-center justify-between rounded-2xl" style={{ ...navGlass, height: 64 }}>
        <Link to="/" className="flex items-center gap-2.5" style={{ fontFamily: 'var(--font-heading)' }}>
          <img src="/tente.ico" alt="Gaked AI" className="w-7 h-7 object-contain" />
          <span className="text-[17px] font-bold text-white" style={{ letterSpacing: '-0.02em' }}>Gaked AI</span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {['Features', 'Prezzi', 'Modelli'].map(label => (
            <a
              key={label}
              href={`#${label.toLowerCase()}`}
              className="px-4 py-2 text-[15px] font-semibold rounded-xl transition-all duration-200"
              style={{ color: 'rgba(255,255,255,0.72)' }}
              onMouseEnter={e => e.currentTarget.style.color = '#fff'}
              onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.72)'}
            >
              {label}
            </a>
          ))}
        </div>

        <Link
          to={isAuthenticated ? '/dashboard' : '/login'}
          className="hidden md:inline-flex items-center px-5 py-2.5 rounded-full text-sm font-bold transition-all duration-200 hover:opacity-90 hover:-translate-y-px"
          style={{
            background: 'rgba(255,255,255,0.92)',
            color: '#000',
            border: '1px solid rgba(255,255,255,0.80)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.5)',
          }}
        >
          {isAuthenticated ? 'Dashboard' : 'Accedi'}
        </Link>
      </div>
    </nav>
  );
}

export default function LandingPage() {
  const { isAuthenticated } = useAuth();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);
  const heroY = useTransform(scrollYProgress, [0, 0.6], [0, 60]);

  return (
    <div className="min-h-screen overflow-hidden" style={{ background: '#000', fontFamily: 'var(--font-body)' }}>
      <LandingNav isAuthenticated={isAuthenticated} />

      {/* ── Hero ── */}
      <section ref={heroRef} className="relative min-h-screen flex flex-col items-center justify-center" style={{ paddingTop: 80 }}>
        <GradientHeroBg />

        <motion.div
          style={{ opacity: heroOpacity, y: heroY }}
          className="relative z-10 max-w-4xl mx-auto px-6 text-center flex flex-col items-center"
        >
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease }}
            className="text-xs font-bold uppercase mb-8 tracking-[0.22em]"
            style={{ color: 'rgba(255,255,255,0.42)', fontFamily: 'var(--font-body)' }}
          >
            AI Image &amp; Video Platform
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, scale: 0.94, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.9, ease }}
            className="font-bold mb-6 text-white"
            style={{
              fontFamily: 'var(--font-heading)',
              fontSize: 'clamp(3.2rem, 8vw, 6.5rem)',
              lineHeight: 1.03,
              letterSpacing: '-0.04em',
            }}
          >
            Genera.{' '}
            <span
              style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(35,136,255,0.9) 50%, rgba(74,58,255,0.85) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Immagina.
            </span>
            <br />Crea.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.18, ease }}
            className="text-lg md:text-xl max-w-lg mx-auto mb-12 leading-relaxed"
            style={{ color: 'rgba(255,255,255,0.50)', fontFamily: 'var(--font-body)' }}
          >
            Immagini e video professionali con i modelli AI più avanzati. Aggiornati in tempo reale, senza limiti.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3, ease }}
            className="flex flex-col sm:flex-row gap-3"
          >
            <Link
              to={isAuthenticated ? '/dashboard' : '/login'}
              className="flex items-center justify-center gap-2 px-8 py-4 rounded-full font-bold text-sm transition-all duration-200 hover:opacity-90 hover:-translate-y-0.5 group"
              style={{
                background: 'rgba(255,255,255,0.94)',
                color: '#000',
                boxShadow: '0 12px 32px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.6)',
                fontFamily: 'var(--font-body)',
              }}
            >
              {isAuthenticated ? 'Vai alla Dashboard' : 'Inizia Gratis'}
              <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
            </Link>

            <a
              href="#features"
              className="flex items-center justify-center gap-2 px-8 py-4 rounded-full font-semibold text-sm transition-all duration-200 hover:border-white/40 hover:bg-white/5"
              style={{
                border: '1px solid rgba(255,255,255,0.18)',
                color: 'rgba(255,255,255,0.82)',
                fontFamily: 'var(--font-body)',
              }}
            >
              Esplora Modelli
            </a>
          </motion.div>
        </motion.div>

        {/* scroll cue */}
        <motion.div
          className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 0.6 }}
        >
          <motion.div
            animate={{ y: [0, 9, 0] }}
            transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
            style={{ width: 1, height: 44, background: 'rgba(255,255,255,0.22)', margin: '0 auto' }}
          />
        </motion.div>
      </section>

      {/* ── Stats bar ── */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, ease }}
        className="relative z-10 border-y"
        style={{ background: 'rgba(255,255,255,0.02)', borderColor: 'rgba(255,255,255,0.07)' }}
      >
        <div className="max-w-5xl mx-auto px-6 py-8 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { n: '1M+', l: 'Immagini Generate' },
            { n: '50K+', l: 'Utenti Attivi' },
            { n: '99.9%', l: 'Uptime' },
            { n: '24/7', l: 'Disponibilità' },
          ].map(({ n, l }) => (
            <div key={l}>
              <div className="text-2xl md:text-3xl font-bold text-white mb-1" style={{ fontFamily: 'var(--font-heading)', letterSpacing: '-0.03em' }}>{n}</div>
              <div className="text-xs uppercase tracking-[0.16em]" style={{ color: 'rgba(255,255,255,0.36)' }}>{l}</div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* ── Features ── */}
      <section id="features" className="relative py-32 px-6" style={{ background: '#080808' }}>
        {/* orb accents */}
        <div aria-hidden className="pointer-events-none absolute right-0 top-1/4 w-96 h-96 opacity-10" style={{ background: 'radial-gradient(circle, rgba(35,136,255,0.8) 0%, transparent 70%)', filter: 'blur(100px)' }} />
        <div aria-hidden className="pointer-events-none absolute -left-20 bottom-1/4 w-[600px] h-96 opacity-10" style={{ background: 'radial-gradient(ellipse, rgba(74,58,255,0.6) 0%, transparent 70%)', filter: 'blur(120px)' }} />

        <div className="relative max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, x: -16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="flex items-center gap-6 mb-6"
          >
            <span className="text-xs font-bold uppercase tracking-[0.22em]" style={{ color: 'rgba(255,255,255,0.32)', whiteSpace: 'nowrap' }}>Cosa puoi fare</span>
            <div className="h-px flex-1" style={{ background: 'rgba(255,255,255,0.08)' }} />
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.06, ease }}
            className="text-4xl md:text-5xl font-bold text-white mb-3"
            style={{ fontFamily: 'var(--font-heading)', letterSpacing: '-0.03em', lineHeight: 1.08 }}
          >
            Tutto ciò di cui hai bisogno,<br />in un unico posto.
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.12 }}
            className="text-base mb-14 max-w-xl"
            style={{ color: 'rgba(255,255,255,0.44)', lineHeight: 1.7 }}
          >
            Un'unica piattaforma per generare immagini, video e molto altro con i migliori modelli AI disponibili.
          </motion.p>

          <div className="grid md:grid-cols-2 gap-4">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={f.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08, ease }}
                  className="relative overflow-hidden rounded-3xl p-7"
                  style={glassCard}
                >
                  <div className="relative flex flex-col gap-4">
                    <div
                      className="w-11 h-11 rounded-2xl flex items-center justify-center"
                      style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}
                    >
                      <Icon size={20} color="rgba(255,255,255,0.85)" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: 'var(--font-heading)', letterSpacing: '-0.02em' }}>{f.title}</h3>
                      <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.44)' }}>{f.description}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section id="prezzi" className="relative py-32 px-6" style={{ background: '#000' }}>
        <div className="relative max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, x: -16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="flex items-center gap-6 mb-6"
          >
            <span className="text-xs font-bold uppercase tracking-[0.22em]" style={{ color: 'rgba(255,255,255,0.32)', whiteSpace: 'nowrap' }}>Piani</span>
            <div className="h-px flex-1" style={{ background: 'rgba(255,255,255,0.08)' }} />
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease }}
            className="text-4xl md:text-5xl font-bold text-white mb-14"
            style={{ fontFamily: 'var(--font-heading)', letterSpacing: '-0.03em', lineHeight: 1.08 }}
          >
            Semplice. Trasparente.<br />Senza sorprese.
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-4">
            {pricingPlans.map((plan, i) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: i * 0.1, ease }}
                whileHover={{ y: -4 }}
                className="relative rounded-3xl p-7 flex flex-col"
                style={plan.featured ? {
                  background: 'rgba(255,255,255,0.07)',
                  border: '1px solid rgba(255,255,255,0.20)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  boxShadow: '0 0 0 1px rgba(255,255,255,0.06), 0 24px 64px rgba(0,0,0,0.5)',
                } : glassCard}
              >
                {plan.featured && (
                  <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold"
                    style={{ background: 'rgba(255,255,255,0.92)', color: '#000' }}
                  >
                    Più Popolare
                  </div>
                )}

                <div className="mb-7">
                  <p className="text-sm font-semibold mb-1" style={{ color: 'rgba(255,255,255,0.48)' }}>{plan.name}</p>
                  <div className="flex items-baseline gap-1.5 mb-2">
                    <span className="text-4xl font-bold text-white" style={{ fontFamily: 'var(--font-heading)', letterSpacing: '-0.04em' }}>€{plan.price}</span>
                    <span className="text-sm" style={{ color: 'rgba(255,255,255,0.36)' }}>{plan.period}</span>
                  </div>
                  <p className="text-sm" style={{ color: 'rgba(255,255,255,0.44)' }}>{plan.description}</p>
                </div>

                <ul className="flex flex-col gap-2.5 mb-8 flex-1">
                  {plan.features.map(feat => (
                    <li key={feat} className="flex items-center gap-2.5 text-sm" style={{ color: 'rgba(255,255,255,0.68)' }}>
                      <Check size={14} style={{ color: 'rgba(255,255,255,0.5)', flexShrink: 0 }} />
                      {feat}
                    </li>
                  ))}
                </ul>

                <Link
                  to={plan.href}
                  className="flex items-center justify-center gap-2 w-full py-3.5 rounded-2xl text-sm font-bold transition-all duration-200 hover:opacity-88 hover:-translate-y-px"
                  style={plan.featured ? {
                    background: 'rgba(255,255,255,0.92)',
                    color: '#000',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                  } : {
                    background: 'rgba(255,255,255,0.06)',
                    color: 'rgba(255,255,255,0.78)',
                    border: '1px solid rgba(255,255,255,0.12)',
                  }}
                >
                  {plan.cta}
                  <ArrowRight size={14} />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <div className="relative overflow-hidden" style={{ background: '#000' }}>
        <GradientBg inverted opacity={0.65} />
        <section className="relative z-10 py-36 px-6 text-center">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="flex items-center justify-center gap-6 mb-8"
            >
              <div className="h-px w-16" style={{ background: 'rgba(255,255,255,0.12)' }} />
              <span className="text-xs font-bold uppercase tracking-[0.22em]" style={{ color: 'rgba(255,255,255,0.32)' }}>Inizia ora</span>
              <div className="h-px w-16" style={{ background: 'rgba(255,255,255,0.12)' }} />
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease }}
              className="text-4xl md:text-6xl font-bold text-white mb-6"
              style={{ fontFamily: 'var(--font-heading)', letterSpacing: '-0.04em', lineHeight: 1.05 }}
            >
              Pronto a creare<br />qualcosa di straordinario?
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.12 }}
              className="text-lg mb-10"
              style={{ color: 'rgba(255,255,255,0.44)' }}
            >
              Unisciti a migliaia di creativi che usano già Gaked AI ogni giorno.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Link
                to={isAuthenticated ? '/dashboard' : '/login'}
                className="inline-flex items-center gap-2.5 px-9 py-4 rounded-full font-bold text-sm transition-all duration-200 hover:opacity-90 hover:-translate-y-0.5 group"
                style={{
                  background: 'rgba(255,255,255,0.94)',
                  color: '#000',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.6)',
                }}
              >
                {isAuthenticated ? 'Vai alla Dashboard' : 'Inizia Gratis — È Gratis'}
                <ArrowUpRight size={15} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer
          className="relative z-10 py-10 px-6"
          style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}
        >
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2.5" style={{ fontFamily: 'var(--font-heading)' }}>
              <img src="/tente.ico" alt="" className="w-6 h-6 object-contain" />
              <span className="text-sm font-bold text-white" style={{ letterSpacing: '-0.02em' }}>Gaked AI</span>
            </div>
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.28)' }}>© 2025 Gaked AI. Tutti i diritti riservati.</p>
            <div className="flex items-center gap-6">
              {['Termini', 'Privacy', 'Contatti'].map(l => (
                <a key={l} href="#" className="text-xs transition-colors duration-200" style={{ color: 'rgba(255,255,255,0.32)' }}
                   onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.7)'}
                   onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.32)'}
                >{l}</a>
              ))}
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
